import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useGetFeedbackItems,
  useUpdateFeedbackItem,
  getGetFeedbackItemsQueryKey,
  type FeedbackItem,
} from "@workspace/api-client-react";
import { useAuthEnabled } from "@/lib/authQuery";

type StatusFilter = "all" | "new" | "triaged" | "closed";
type Status = "new" | "triaged" | "closed";

const STATUS_LABELS: Record<Status, string> = {
  new: "New",
  triaged: "Triaged",
  closed: "Closed",
};

const CATEGORY_STYLES: Record<string, string> = {
  bug:     "bg-rose-100 text-rose-700 border-rose-200",
  general: "bg-sky-100 text-sky-700 border-sky-200",
  feature: "bg-emerald-100 text-emerald-700 border-emerald-200",
};

function formatDate(d: string | Date): string {
  const date = d instanceof Date ? d : new Date(d);
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function FeedbackRow({
  item,
  onUpdate,
  isPending,
}: {
  item: FeedbackItem;
  onUpdate: (id: number, patch: { status?: Status; adminNotes?: string | null }) => void;
  isPending: boolean;
}) {
  const [notes, setNotes] = useState(item.adminNotes ?? "");
  const initialNotes = item.adminNotes ?? "";

  return (
    <div
      className="rounded-2xl border border-stone-200 bg-white p-4"
      data-testid={`feedback-row-${item.id}`}
    >
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <span
          className={
            "rounded-full border px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide " +
            (CATEGORY_STYLES[item.category] ?? "bg-stone-100 text-stone-700 border-stone-200")
          }
        >
          {item.category}
        </span>
        <span className="text-xs text-stone-400">#{item.id} · {formatDate(item.createdAt)}</span>
        <div className="ml-auto flex items-center gap-2">
          <select
            value={item.status}
            onChange={(e) => onUpdate(item.id, { status: e.target.value as Status })}
            disabled={isPending}
            className="rounded-xl border border-stone-200 bg-white px-2.5 py-1 text-xs font-medium text-stone-700 focus:border-stone-400 focus:outline-none disabled:opacity-50"
            data-testid={`feedback-status-${item.id}`}
          >
            {(Object.keys(STATUS_LABELS) as Status[]).map((s) => (
              <option key={s} value={s}>{STATUS_LABELS[s]}</option>
            ))}
          </select>
        </div>
      </div>

      <p className="whitespace-pre-wrap text-sm text-stone-800 leading-relaxed">{item.message}</p>

      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-stone-500">
        <span>
          <span className="text-stone-400">From:</span>{" "}
          {item.submitterUsername ? (
            <span className="font-medium text-stone-700">@{item.submitterUsername}</span>
          ) : item.contactEmail ? (
            <a href={`mailto:${item.contactEmail}`} className="text-stone-700 hover:underline">
              {item.contactEmail}
            </a>
          ) : (
            <span className="text-stone-500">anonymous</span>
          )}
        </span>
        {item.submitterUsername && item.contactEmail && (
          <span>
            <span className="text-stone-400">Email:</span>{" "}
            <a href={`mailto:${item.contactEmail}`} className="text-stone-700 hover:underline">
              {item.contactEmail}
            </a>
          </span>
        )}
        {item.pageUrl && (
          <span className="truncate">
            <span className="text-stone-400">Page:</span>{" "}
            <a href={item.pageUrl} target="_blank" rel="noreferrer" className="text-stone-700 hover:underline">
              {item.pageUrl}
            </a>
          </span>
        )}
      </div>

      <textarea
        value={notes}
        onChange={(e) => setNotes(e.target.value.slice(0, 4000))}
        onBlur={() => {
          if (notes !== initialNotes) onUpdate(item.id, { adminNotes: notes || null });
        }}
        placeholder="Admin notes (saved on blur)"
        rows={2}
        className="mt-3 w-full resize-none rounded-xl border border-stone-200 bg-stone-50 px-3 py-2 text-xs text-stone-700 focus:border-stone-400 focus:bg-white focus:outline-none"
        data-testid={`feedback-notes-${item.id}`}
      />
    </div>
  );
}

export function AdminFeedbackSection() {
  const queryClient = useQueryClient();
  const authed = useAuthEnabled();
  const [filter, setFilter] = useState<StatusFilter>("new");

  const params = filter === "all" ? undefined : { status: filter as Status };
  const queryKey = getGetFeedbackItemsQueryKey(params);

  const { data, isLoading } = useGetFeedbackItems(params, {
    query: { queryKey, enabled: authed },
  });

  const { mutate, isPending } = useUpdateFeedbackItem({
    mutation: {
      onSuccess: () => {
        // Invalidate the generated key prefix so every status-filter variation
        // refetches (orval prefixes keys with the API base path).
        queryClient.invalidateQueries({
          queryKey: getGetFeedbackItemsQueryKey().slice(0, 1),
        });
      },
    },
  });

  const items = data ?? [];

  return (
    <section className="mt-8 rounded-3xl border border-stone-200 bg-white p-6 shadow-sm">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-xl font-semibold text-stone-900">User feedback</h3>
          <p className="text-sm text-stone-500 mt-0.5">
            Bug reports, feature requests, and general notes from the Feedback button.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {(["new", "triaged", "closed", "all"] as StatusFilter[]).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={
                "rounded-xl px-3 py-1.5 text-xs font-medium transition " +
                (filter === f
                  ? "bg-stone-900 text-white"
                  : "bg-stone-100 text-stone-600 hover:bg-stone-200")
              }
              data-testid={`feedback-filter-${f}`}
            >
              {f === "all" ? "All" : STATUS_LABELS[f as Status]}
            </button>
          ))}
        </div>
      </div>

      {isLoading && (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-24 animate-pulse rounded-2xl bg-stone-100" />
          ))}
        </div>
      )}

      {!isLoading && items.length === 0 && (
        <div className="rounded-2xl border border-dashed border-stone-200 p-8 text-center">
          <p className="text-stone-500">No feedback in this view.</p>
        </div>
      )}

      {!isLoading && items.length > 0 && (
        <div className="space-y-3">
          {items.map((item) => (
            <FeedbackRow
              key={item.id}
              item={item}
              isPending={isPending}
              onUpdate={(id, patch) => mutate({ id, data: patch })}
            />
          ))}
        </div>
      )}
    </section>
  );
}
